import React, {useState} from 'react';
import {useSelector} from "react-redux";
import {fetchCourseContent} from "../courseContent/courseObjects";
import {CheckCircle, Error, Help, Info, Redo, ExpandMore} from "@mui/icons-material";
import {Flex} from "reflexbox";
import Typography from "@mui/material/Typography";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from "@mui/material/Paper";
import CustomTooltip from "../common/CustomTooltip";
import Box from "@mui/material/Box";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import { alpha, useTheme } from '@mui/material/styles';
import {ANALAGOUSCOLOR1} from "../helpers/ThemeSettings";

const DetailedQuestionBreakdown = (props) => {
  const theme = useTheme();
  const teamDetails = useSelector((state) => state.team?.teamDetails) || {};
  const questionHistory = teamDetails?.questions || {};
  const courseName = props.courseName || teamDetails?.currentCourseSelection;
  const courseContent = fetchCourseContent(courseName);
  const [expanded, setExpanded] = useState(false);

  if (!courseContent) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          Course not found: {courseName}
        </Typography>
        <Typography>
          Please select a valid course from the course selector.
        </Typography>
      </div>
    );
  }

  const cellStyle = {
    wordWrap: 'break-word',
    maxWidth: '120px',
    padding: '16px 8px'
  };
  //icons
  const icons = {
    Success: <CheckCircle fontSize="small" style={{color: 'green'}} key={1}/>,
    Hint: <Help fontSize="small" style={{color: 'darkorange'}} key={2}/>,
    Incorrect: <Error fontSize="small" style={{color: 'darkred'}} key={3}/>,
    Skip: <Redo fontSize="small" sx={{ color: 'text.secondary' }} key={4}/>,
  };
  // const success = <CheckCircle fontSize="small" style={{color: 'green'}}/>;
  // const hint = <Help fontSize="small" style={{color: 'orange'}}/>;
  // const incorrectIcon = <Error fontSize="small" style={{color: 'red'}}/>;
  // const skipIcon = <PlayCircleFilled fontSize="small" style={{color: 'gray'}}/>;
  const iconLegend = [];
  for (const [key, value] of Object.entries(icons)) {
    iconLegend.push(<Flex key={key} style={{
      paddingRight: '3px',
      paddingTop: '4px',
      alignItems: 'center'
    }}>{key}: {value}{key !== 'Skip' && ","} </Flex>)
  }

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const tableWrapper = (currentLocation, tableRows, pointsForLocation) => (<>
    <Accordion expanded={expanded === currentLocation} onChange={handleChange(currentLocation)}  >
      <AccordionSummary expandIcon={<ExpandMore />} style={{ backgroundColor: '#fafafa'}}>
        <Typography variant={'h6'} style={{color: ANALAGOUSCOLOR1}}>{currentLocation}</Typography>
      </AccordionSummary>
      <AccordionDetails style={{padding: 0}}>
        <TableContainer component={Paper} style={{minHeight: '50px'}}>
          <Table aria-label="simple table" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell style={{...cellStyle, fontWeight: 'bold'}}>Question </TableCell>
                <TableCell align="right" style={{...cellStyle, fontWeight: 'bold'}}>Points</TableCell>
                <TableCell align="left" style={{...cellStyle, fontWeight: 'bold'}}>
                  <Flex style={{alignItems: 'center', fontWeight: 'bold'}}>
                    <Box mr={'2px'}>Attempts</Box> <CustomTooltip title={iconLegend}
                                                                  triggerComponent={<Info fontSize="small"
                                                                                          color={'primary'}/>}/>
                  </Flex>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableRows}
              <TableRow>
                <TableCell colSpan="3" style={{textAlign: 'right', fontWeight: 'bold'}}> Total Points:  {pointsForLocation} </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  </>);

  let pointsForLocation = 0;
  const tableContent = [];
  let tableRows = [];
  //loop that adds up how many questions there are and how many are correct
  let currentLocation = null;
  for (const [key, value] of Object.entries(courseContent)) {
    //conditional statement needed to filter out results for other courses
    if (questionHistory.hasOwnProperty(key)) {
      //get location for rows
      if (!currentLocation) {
        currentLocation = value?.location.name;
      } else if (currentLocation !== value.location?.name) {
        //push table content so we start a new table if the location is new
        tableContent.push(<React.Fragment key={key}>
          {tableWrapper(currentLocation, tableRows, pointsForLocation)}
        </React.Fragment>);
        currentLocation = value?.location.name;
        tableRows = [];
        pointsForLocation = 0;
      }
      if (questionHistory[key].hasOwnProperty('userActions')) {
        const {userActions, pointsScored} = questionHistory[key];
        if (userActions.includes('success')) {
          pointsForLocation += pointsScored;
        }
        tableRows.push(<TableRow key={key}
                                 style={{
                                   backgroundColor: userActions[userActions.length - 1] === 'success'
                                     ? alpha(theme.palette.success.main, 0.14)
                                     : alpha(theme.palette.text.primary, 0.06),
                                 }}>
          <TableCell component="th" scope="row" style={cellStyle}> {value?.displayName || key} </TableCell>
          <TableCell align="right">{pointsScored === 0 ? '-' : pointsScored}</TableCell>
          <TableCell align="left">
            <Flex>
            {userActions.map((value, key) => (<div key={key}>
                {value === 'success' && <CustomTooltip title={'success'} style={{width: '100%'}}> {icons.Success} </CustomTooltip>}
                {value === 'hint' && <CustomTooltip title={'hint'} style={{width: '100%'}}> {icons.Hint} </CustomTooltip>}
                {value === 'incorrect' && <CustomTooltip title={'incorrect'} style={{width: '100%'}}> {icons.Incorrect} </CustomTooltip>}
                {value === 'skip' && <CustomTooltip title={'skip'} style={{width: '100%'}}> {icons.Skip} </CustomTooltip>}
              </div>))}
            </Flex>
          </TableCell>
          {/*<TableCell align="right">{}na</TableCell>*/}
        </TableRow>)
      }
    }
  }
  tableContent.push(<React.Fragment key={'default'}>
    {tableWrapper(currentLocation, tableRows, pointsForLocation)}
  </React.Fragment>);
  return (
    <>
      <Flex style={{marginBottom: '12px'}}>{iconLegend}</Flex>
      {tableContent}
    </>
  )
};

export default DetailedQuestionBreakdown;